import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LuChevronDown, LuCoffee, LuShieldCheck, LuSparkles, LuSprout } from "react-icons/lu";
import { FiHelpCircle } from "react-icons/fi";

const FAQ_DATA = [
  {
    id: "coffe-diff",
    question: "What makes Lounge Coffe different from ordinary coffee?",
    answer:
      "Lounge Coffe is a precision-engineered experience. We begin with exceptionally high-altitude, single-origin organic beans, which are slowly custom-roasted to match the four distinct bio-rhythms of your day. It's not just caffeine; it's a sensory calibration designed for deep focus.",
    icon: LuCoffee,
  },
  {
    id: "temp-retention",
    question: "What is the secret behind the multi-hour temperature retention?",
    answer:
      "Our specialized cups employ a dual-wall high-vacuum copper barrier. This state-of-the-art construction locks in aroma and heat for up to 8 hours, and preserves refreshing cold brews for up to 18 hours, without transmitting any heat or sweat to your hands.",
    icon: LuSparkles,
  },
  {
    id: "eco-materials",
    question: "Are your cups completely organic and biodegradable?",
    answer:
      "Absolutely. Environmental responsibility is baked into our design. Our modern paper-feel cups are interior-lined with plant-derived PLA (polylactic acid) rather than conventional petroleum plastic. They are 100% compostable, ocean-safe, and free from microplastics.",
    icon: LuSprout,
  },
  {
    id: "interactive-timeline",
    question: "How does the interactive timeline map to actual brewing times?",
    answer:
      "The Lounge Coffee timeline transitions in sync with daylight cycles: from Canyon Crimson morning espressos, to Forest Green midday matchas, Amber Gold afternoon lattes, and deep Midnight Espresso, offering a customized sensory landscape for every hour.",
    icon: LuShieldCheck,
  },
];

export default function Faq({ className }) {
  const [openId, setOpenId] = useState("coffe-diff");
  const [_hoveredId, setHoveredId] = useState(null);

  const toggleItem = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <section
        id="faq-section"
        className={"relative w-full py-20 sm:py-28 px-6 sm:px-12 md:px-16 flex flex-col items-center justify-center overflow-hidden border-t border-[#2B170A]/10 " + (className || "")}
        style={{
          backgroundColor: "#FAF4EA",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* Dynamic atmospheric ambient glow */}
        <div
          className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full blur-[160px] pointer-events-none opacity-40 transition-all duration-1000"
          style={{ backgroundColor: openId ? "#FAF4EA" : "#EFE7DB" }}
        />

        <div className="w-full max-w-[850px] flex flex-col items-center gap-12 sm:gap-16 relative z-10">
          {/* Top: Bold Aesthetic Centered Header Block */}
          <div className="w-full flex flex-col items-center text-center gap-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#2B170A]/5 border border-[#2B170A]/10">
              <FiHelpCircle className="w-4 h-4 text-[#2B170A]/80" />
              <span className="text-[10px] sm:text-[11px] font-mono font-bold tracking-widest text-[#2B170A] uppercase">
                Lounge Knowledge Base
              </span>
            </div>

            <h2
              className="font-extrabold uppercase text-[#2B170A] tracking-tight leading-[1.05] text-center"
              style={{
                fontSize: "clamp(32px, 5vw, 52px)",
                fontWeight: 800,
              }}
            >
              FREQUENTLY BREWED QUESTIONS
            </h2>

            <div className="w-20 border-t-2 border-[#2B170A]/30 my-2" />

            <p className="text-sm sm:text-base text-[#2B170A]/70 leading-relaxed max-w-xl mx-auto">
              Everything you need to know about our organic sourcing, thermoregulation mechanics, and customized design offerings. Can't find your answer? Drop us an inquiry anytime.
            </p>
          </div>

          {/* Bottom: Sleek Centered Accordion Interface */}
          <div className="w-full flex flex-col gap-4">
            {FAQ_DATA.map((item) => {
              const isOpen = openId === item.id;
              const IconComponent = item.icon;

              return (
                <div
                  key={item.id}
                  className="group relative rounded-2xl border transition-all duration-300 overflow-hidden"
                  style={{
                    backgroundColor: isOpen ? "rgba(255, 255, 255, 0.45)" : "rgba(255, 255, 255, 0.15)",
                    borderColor: isOpen ? "rgba(43, 23, 10, 0.15)" : "rgba(43, 23, 10, 0.05)",
                    boxShadow: isOpen ? "0 10px 30px -15px rgba(43, 23, 10, 0.08)" : "none",
                  }}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Accordion Trigger Header */}
                  <button
                    type="button"
                    onClick={() => toggleItem(item.id)}
                    className="w-full text-left p-6 sm:p-7 flex items-center justify-between gap-4 pointer-events-auto select-none"
                  >
                    <div className="flex items-center gap-4">
                      {/* Floating Icon Base */}
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300"
                        style={{
                          backgroundColor: isOpen ? "#2B170A" : "rgba(43, 23, 10, 0.05)",
                          color: isOpen ? "#FAF4EA" : "#2B170A",
                          transform: isOpen ? "scale(1.05)" : "scale(1)",
                        }}
                      >
                        <IconComponent className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <span className="text-base sm:text-lg font-bold text-[#2B170A] transition-colors duration-300 group-hover:text-[#2B170A]">
                        {item.question}
                      </span>
                    </div>

                    {/* Elegant rotating custom chevron */}
                    <div
                      className="w-8 h-8 rounded-full border border-[#2B170A]/10 flex items-center justify-center text-[#2B170A]/70 group-hover:bg-[#2B170A]/5 transition-all duration-300 shrink-0"
                      style={{
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        backgroundColor: isOpen ? "rgba(43, 23, 10, 0.04)" : "transparent",
                      }}
                    >
                      <LuChevronDown className="w-4 h-4" strokeWidth={2.2} />
                    </div>
                  </button>

                  {/* Animated Collapsible Expand Content */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key={"content-" + item.id}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <div className="px-6 pb-7 sm:px-7 sm:pb-8 pl-16 sm:pl-20">
                          {/* Divider */}
                          <div className="w-full border-t border-[#2B170A]/10 mb-4" />
                          <p className="text-sm sm:text-[15px] text-[#2B170A]/85 leading-relaxed font-normal">
                            {item.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}